import React, { useEffect, useState } from "react";
import { mediaApi, resolveMediaSrc } from "@/lib/api";
import { X, Upload, Trash2, Loader2, FileText } from "lucide-react";

const ACCEPT = {
  image: "image/*,video/*",
  video: "video/*",
  doc: "*",
};

const fileExt = (name = "") => {
  const m = String(name).split(".").pop();
  return m && m.length <= 5 ? m.toUpperCase() : "FILE";
};

const MediaPicker = ({ open, onClose, onPick, kind = "image" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  // Image slots accept photos AND videos; menus (doc) show every file type.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const listParams = kind === "doc" || kind === "image" ? {} : { kind };
    (async () => {
      try {
        const { data } = await mediaApi.get(`/api/media`, { params: listParams });
        const list = kind === "image" ? data.filter((m) => m.kind === "image" || m.kind === "video") : data;
        if (alive) setItems(list);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, kind, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await mediaApi.post("/api/media/upload", fd, { timeout: 0 });
      reload();
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this file? This cannot be undone.")) return;
    await mediaApi.delete(`/api/media/${id}`);
    reload();
  };

  if (!open) return null;

  const noun = kind === "doc" ? "file (any type)" : kind === "image" ? "photo or video" : kind;

  return (
    <div data-testid="media-picker" className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center px-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <h2 className="text-base font-medium text-zinc-900">Media Library</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Pick an existing {noun} or upload a new one.</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer inline-flex items-center gap-2 bg-zinc-900 text-white text-xs tracking-wide px-3 py-2 rounded hover:bg-zinc-800" data-testid="media-picker-upload-btn">
              {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
              Upload
              <input type="file" hidden accept={ACCEPT[kind] || "image/*"} onChange={onUpload} disabled={uploading} />
            </label>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="media-picker-close"><X size={20} /></button>
          </div>
        </div>

        {error && <div className="mx-6 mt-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</div>}

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-zinc-400" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 text-sm text-zinc-500">Nothing here yet. Upload your first file above.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {items.map((m) => (
                <div key={m.id} className="group relative border border-zinc-200 rounded-md overflow-hidden bg-zinc-50">
                  <button
                    type="button"
                    onClick={() => { onPick(m.kind === "video" ? `${m.url}#video` : m.url); onClose(); }}
                    className="block w-full aspect-square"
                    data-testid={`media-pick-${m.id}`}
                  >
                    {m.kind === "video" ? (
                      <video src={resolveMediaSrc(m.url)} className="w-full h-full object-cover" />
                    ) : m.kind === "doc" ? (
                      <span className="w-full h-full flex flex-col items-center justify-center gap-2 text-[#942222]">
                        <FileText size={28} />
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500">{fileExt(m.filename)}</span>
                      </span>
                    ) : (
                      <img src={resolveMediaSrc(m.url)} alt={m.filename} className="w-full h-full object-cover" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(m.id)}
                    className="absolute top-2 right-2 bg-white/90 hover:bg-white text-red-600 rounded p-1 opacity-0 group-hover:opacity-100 transition"
                    title="Delete"
                    data-testid={`media-delete-${m.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                  <div className="px-2 py-1 text-[10px] text-zinc-600 truncate">{m.filename}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaPicker;
